<template>
  <EditableTable
    ref="editer"
    :disabled="props.pageMode === 'Detail'"
    :actionHandler="actionHandler"
    @table-data-change="onTableFieldsChange"
  >
    <template #tableAfter>
      <div ref="container"> </div>
      <BasePageModal
        :getContainer="() => $refs.container"
        @register="registerModal"
        @ok="onModalOk"
      />
    </template>
  </EditableTable>
</template>

<script setup lang="ts">
  import { computed, nextTick, Ref, ref, watch } from 'vue';
  import { getRowSpanCount, omit } from '@mts-fe/utils';
  import { getMappingData } from '@mts/utils';
  import {
    ActionHandlerFn,
    FormTabProps,
    PageModalPayload,
    RenderTableColumn,
    useActionHandler,
    useColumnFetch,
    usePageModal,
    useSchemaActionContext,
    useSchemaPageContext,
  } from '..';
  import BasePageModal from './BasePageModal.vue';
  import EditableTable from './EditableTable.vue';
  import type { EditableTableExposed } from './pc';

  defineOptions({
    name: 'TabTable',
  });

  const props = withDefaults(defineProps<FormTabProps>(), {
    title: '',
    size: 'small',
    inset: true,
    editable: true,
    ellipsis: true,
    bordered: true,
    canResize: true,
    showHeader: true,
    autoCreateKey: true,
    emptyDataIsShowTable: true,
  });

  const editer: Ref<EditableTableExposed | null> = ref(null);
  const editColumns = ref<RenderTableColumn[]>([]);
  const onEditorOk = () => {
    // @ts-ignore
    editColumns.value = props.columns?.filter((col) => !!col.editComponent);
    const tableProps = {
      ...omit(props, ['pageMode', 'pageData', 'setTabData', 'getPageData']),
      actions: props.actions?.map((col) => ({
        ...col,
        ifShow: !col['hidden' + props.pageMode],
        disabled: col['disabled' + props.pageMode],
      })),
      columns: props.columns?.map((col) => ({
        ...col,
        ifShow: !col['hidden' + props.pageMode],
        editDynamicDisabled: col['disabled' + props.pageMode],
      })),
    };
    if (tableProps.rowSelection?.type) {
      tableProps.rowSelection.onChange = (selectedRowKeys) =>
        props.setTabData(props.relationKey + '@selectedRowKeys', selectedRowKeys);
    }
    editer.value?.setProps(tableProps);

    useColumnFetch(props.columns, editer.value!);
    initData();
  };
  watch(editer, onEditorOk, { once: true });

  const initData = () => {
    const tableData = props.pageData[props.relationKey] || [];
    // console.log(editer.value, props.relationKey, props, tableData.length);
    if (tableData.length && tableData.length !== editer.value?.getDataSource().length) {
      const data = props.pageMode === 'Copy' ? tableData.map(({ id, ...i }) => i) : tableData;
      nextTick(() => {
        editer.value?.setTableData(data.length ? data : [{}]);
        nextTick(() => onTableFieldsChange(editer.value?.getDataSource() || data));
      });
    }
  };
  watch(() => props.relationKey && Object.keys(props.pageData).length, initData);

  const onTableFieldsChange = (dataSource: Recordable[]) => {
    props.setTabData(props.relationKey, dataSource);

    const columns = editer.value?.getColumns() as RenderTableColumn[];
    if (columns.filter((column) => column.mergeCellBy).length) {
      const mergedColumns = columns.map((column) => {
        if (column.mergeCellBy) {
          // 单元格合并
          const keys = [...column.mergeCellBy.split(','), column.dataIndex!.toString()];
          column.customCell = (_, index) => ({
            rowSpan: getRowSpanCount(dataSource, keys, index),
          });
        }
        return column;
      });
      editer.value?.setColumns(mergedColumns);
    }
  };

  const onModalOk = async (data: any, { actionEvent, record }: PageModalPayload) => {
    console.log(actionEvent, data, { ...record });
    if (record?.__key__) {
      const mapping =
        editColumns.value.filter((col) => col.fieldmapping?.length)[0]?.fieldmapping || [];
      editer.value?.updateTableDataRecord(record.__key__, getMappingData(mapping, data), true);
    }
  };

  const pageContext = useSchemaPageContext();
  const [registerModal, { setModals, showModal }] = usePageModal('TabTable');
  setModals(pageContext.modalsRef?.value);

  const actionContext = useSchemaActionContext();
  const actionRef = computed(() => editer.value);
  const { apiActionHandler, tableActionHandler } = useActionHandler({
    showModal,
    pageContext,
    actionContext,
    actionRef,
  });
  const actionHandler: ActionHandlerFn = async (actionEvent, record, index) => {
    const res = await tableActionHandler(actionEvent, record, index, async () => {
      onTableFieldsChange(editer.value?.getDataSource() || []);
    });
    return typeof res === 'boolean' && !res
      ? apiActionHandler(actionEvent, { ...props.getPageData(), __key__: record?.key }, index)
      : void 0;
  };
</script>
